import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, Shield, Star, Gift, CheckCircle, XCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { validateBetaCode, redeemBetaCode } from '../services/betaCodeService'
import Section from '../components/Section'
import Button from '../components/Button'
import Card from '../components/Card'
import WLogo from '../components/WLogo'

export default function Signup() {
    const navigate = useNavigate()
    const { signup } = useAuth()
    const [_isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        referral: '',
        betaCode: '',
    })
    const [betaCodeStatus, setBetaCodeStatus] = useState<{
        checked: boolean;
        valid: boolean;
        plan?: string;
        description?: string;
        error?: string;
    }>({ checked: false, valid: false })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            // Redeem beta code if entered and valid
            if (formData.betaCode && betaCodeStatus.valid) {
                redeemBetaCode(formData.betaCode)
            }
            await signup(formData.name, formData.email, formData.password, formData.betaCode || undefined)
            navigate('/app')
        } catch {
            console.error('Signup failed')
        } finally {
            setIsLoading(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))

        // Validate beta code as user types
        if (name === 'betaCode') {
            if (value.trim()) {
                const result = validateBetaCode(value)
                setBetaCodeStatus({
                    checked: true,
                    valid: result.valid,
                    plan: result.plan,
                    description: result.description,
                    error: result.error,
                })
            } else {
                setBetaCodeStatus({ checked: false, valid: false })
            }
        }
    }

    const benefits = [
        'Unlimited voice capture and transcription',
        'Agentic auto-scheduler with smart planning',
        'Chief of Staff memory with context recall',
        'Integration with Google Calendar, Slack, Gmail, and more',
        'Mobile, desktop, and web access',
        'Priority customer support',
    ]

    return (
        <Section className="py-12 lg:py-20">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                    {/* Left - Form */}
                    <div>
                        <div className="text-center lg:text-left mb-8">
                            <Link to="/" className="inline-flex items-center gap-2 mb-8">
                                <WLogo className="w-8 h-8" />
                                <span className="text-xl font-semibold text-primary-color">Way2Will</span>
                            </Link>
                            <h1 className="text-3xl lg:text-4xl font-bold text-primary-color mb-4">
                                Start Your 14-Day Free Trial
                            </h1>
                            <p className="text-lg text-secondary-color">
                                Experience the AI Chief of Staff. No credit card required.
                            </p>
                        </div>

                        {/* Trust Badges */}
                        <div className="flex flex-wrap gap-4 mb-8">
                            {['Full Feature Access', 'Cancel Anytime', 'No Credit Card'].map((badge) => (
                                <div key={badge} className="flex items-center gap-2 glass px-3 py-2 rounded-full">
                                    <Check className="w-4 h-4 text-success" />
                                    <span className="text-sm text-secondary-color">{badge}</span>
                                </div>
                            ))}
                        </div>

                        {/* Signup Form */}
                        <Card variant="elevated" className="p-8">
                            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="name" className="text-sm font-medium text-primary-color">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                        className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                        required
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label htmlFor="email" className="text-sm font-medium text-primary-color">
                                        Work Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="your@email.com"
                                        className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                        required
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label htmlFor="password" className="text-sm font-medium text-primary-color">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Create a secure password"
                                        className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                        required
                                        minLength={8}
                                    />
                                    <p className="text-xs text-tertiary-color">Minimum 8 characters</p>
                                </div>

                                {/* Beta Code Field */}
                                <div className="flex flex-col gap-2">
                                    <label htmlFor="betaCode" className="text-sm font-medium text-primary-color flex items-center gap-2">
                                        <Gift className="w-4 h-4 text-primary-base" />
                                        Beta Code <span className="text-tertiary-color">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="betaCode"
                                        name="betaCode"
                                        value={formData.betaCode}
                                        onChange={handleChange}
                                        placeholder="Enter beta code for premium access"
                                        className={`px-4 py-3 glass rounded-lg focus:outline-none ${betaCodeStatus.checked
                                                ? betaCodeStatus.valid
                                                    ? 'border-2 border-green-500'
                                                    : 'border-2 border-red-400'
                                                : 'focus:border-primary-base/25'
                                            }`}
                                    />
                                    {betaCodeStatus.checked && (
                                        <div className={`flex items-center gap-2 text-sm ${betaCodeStatus.valid ? 'text-green-600' : 'text-red-500'}`}>
                                            {betaCodeStatus.valid ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span>✨ {betaCodeStatus.description} - Unlimited {betaCodeStatus.plan?.toUpperCase()} access!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="w-4 h-4" />
                                                    <span>{betaCodeStatus.error}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label htmlFor="referral" className="text-sm font-medium text-primary-color">
                                        How did you hear about us? <span className="text-tertiary-color">(optional)</span>
                                    </label>
                                    <select
                                        id="referral"
                                        name="referral"
                                        value={formData.referral}
                                        onChange={handleChange}
                                        className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                    >
                                        <option value="">Select an option</option>
                                        <option value="search">Search Engine</option>
                                        <option value="social">Social Media</option>
                                        <option value="friend">Friend/Colleague</option>
                                        <option value="blog">Blog/Article</option>
                                        <option value="youtube">YouTube</option>
                                        <option value="podcast">Podcast</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <Button type="submit" size="lg" className="w-full">
                                    {betaCodeStatus.valid ? '🎉 Start with Premium Access' : 'Start Free Trial'}
                                </Button>

                                <p className="text-xs text-center text-tertiary-color">
                                    By signing up, you agree to our{' '}
                                    <Link to="/app/terms" className="text-primary-base hover:underline">Terms of Service</Link>
                                    {' '}and{' '}
                                    <Link to="/app/privacy" className="text-primary-base hover:underline">Privacy Policy</Link>
                                </p>
                            </form>

                            {/* Social Sign Up */}
                            <div className="mt-8">
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-primary-base/10"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="bg-white px-4 text-sm text-tertiary-color">Or sign up with</span>
                                    </div>
                                </div>

                                <div className="flex gap-4 mt-6">
                                    <button className="flex-1 glass py-3 rounded-lg hover:bg-white/75 transition-colors flex items-center justify-center gap-2">
                                        <span className="text-lg">G</span>
                                        <span className="text-sm text-secondary-color">Google</span>
                                    </button>
                                    <button className="flex-1 glass py-3 rounded-lg hover:bg-white/75 transition-colors flex items-center justify-center gap-2">
                                        <span className="text-lg">M</span>
                                        <span className="text-sm text-secondary-color">Microsoft</span>
                                    </button>
                                    <button className="flex-1 glass py-3 rounded-lg hover:bg-white/75 transition-colors flex items-center justify-center gap-2">
                                        <span className="text-lg">🍎</span>
                                        <span className="text-sm text-secondary-color">Apple</span>
                                    </button>
                                </div>
                            </div>
                        </Card>

                        <p className="text-center text-sm text-tertiary-color mt-6">
                            Already have an account?{' '}
                            <a href="#" className="text-primary-base hover:underline">Log in</a>
                        </p>
                    </div>

                    {/* Right - Benefits */}
                    <div className="lg:pt-24">
                        {/* What You'll Get */}
                        <Card variant="default" className="p-8 mb-8">
                            <h3 className="text-xl font-semibold text-primary-color mb-6">What You'll Get:</h3>
                            <ul className="flex flex-col gap-4">
                                {benefits.map((benefit, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                                        <span className="text-secondary-color">{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>

                        {/* Trial Details */}
                        <Card variant="elevated" className="p-8 mb-8">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-xl">⏰</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-primary-color mb-2">Your Trial Details</h3>
                                    <p className="text-secondary-color text-sm mb-3">
                                        Start with full Pro access for 14 days. After your trial, you'll automatically move to our free tier unless you upgrade. No charges, no surprises.
                                    </p>
                                    <Link to="/pricing" className="text-sm text-primary-base hover:underline">
                                        Learn more about pricing →
                                    </Link>
                                </div>
                            </div>
                        </Card>

                        {/* Testimonial */}
                        <Card variant="default" className="p-8 mb-8">
                            <div className="flex gap-1 mb-4">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star key={star} className="w-5 h-5 fill-warning text-warning" />
                                ))}
                            </div>
                            <p className="text-secondary-color italic mb-4">
                                "Way2Will gave me 12 hours back per week. The trial sold itself."
                            </p>
                            <p className="text-sm font-semibold text-primary-color">— Sarah C., Founder</p>
                            <p className="text-xs text-tertiary-color mt-2">Based on 2,000+ reviews</p>
                        </Card>

                        {/* Security */}
                        <div className="flex items-center gap-3 text-center justify-center">
                            <Shield className="w-5 h-5 text-success" />
                            <p className="text-sm text-secondary-color">
                                Your data is encrypted and secure. We're GDPR and BIPA compliant.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    )
}
